import React, { useMemo, useState, useEffect } from "react"
import Button from "../../../../Form/Button"
import Input from "../../../../Form/Input"
import ReusableSelect from "components/Form/ReusableSelect"
import nationalities from "../../../../../seed/nationality/nationality.json"
import country from "../../../../../seed/country/country.json"
import city from "../../../../../seed/city/city.json"
import VerifyAccModal from "components/Modal/VerifyAccModal"
import { useUserContext } from "context/UserContext"
import { useLang } from "hooks/useLang"
import InputNew from "components/Form/newAuthInput"
import ICustomer, { Gender } from "types/ICustomer"
import { validations } from "validation/profile"
import useFormValidation from "validation/hook/useFormValidation"
import PhoneInput from "components/atoms/PhoneInput/newAuthPhoneInput"
import DeleteAccount from "components/DeleteAccount"
import Swal from "sweetalert2"
import DateOfBirthInput from "components/Form/dob"
import dayjs from "dayjs"

interface IProps {
    user: ICustomer
}

interface IForm {
    first_name: string
    last_name: string
    callingCode: string
    phone: string
    email: string
    nationality: string
    country: string
    city: string
    gender: Gender | ""
    birth_date: string | Date
    // password: string
    // passwordConf: string
}

export default function AccountTab({ user }: IProps) {
    const { t } = useLang()
    const [openModal, setOpenModal] = useState(false)
    const [mounted, setMounted] = useState(false)

    const userNationality = useMemo(() => {
        if (!user?.nationality) return null
        return nationalities.find(
            (n) =>
                n.label === user.nationality ||
                n.value === user.nationality ||
                n.label_ar === user.nationality
        )
    }, [user?.nationality])

    const userCountry = useMemo(() => {
        if (!user?.address?.country) return null
        return country.find(
            (c) =>
                c.label === user.address.country ||
                c.value === user.address.country ||
                c.label_ar === user.address.country
        )
    }, [user?.address?.country])

    const userCity = useMemo(() => {
        if (!user?.address?.city) return null
        return city.find(
            (c) =>
                c.label === user.address.city ||
                c.value === user.address.city ||
                c.label_ar === user.address.city
        )
    }, [user?.address?.city])

    // Update the initial form state
    const form: IForm = {
        first_name: user?.first_name || "",
        last_name: user?.last_name || "",
        callingCode: user?.contact?.callingCode || "966",
        phone: user?.contact?.phone || "",
        email: user?.contact?.email || "",
        nationality: userNationality?.label || "Saudi",
        country: userCountry?.label || "Saudi Arabia",
        city: userCity?.label || "Jeddah",
        gender: user?.gender || "",
        birth_date: user?.birth_date || dayjs().format("YYYY-MM-DD"),
    }

    // Remove the empty keys so the backend don't throw empty value error
    function cleanObject(obj) {
        return Object.entries(obj).reduce((acc, [key, value]) => {
            if (value) {
                acc[key] = value
            }
            return acc
        }, {})
    }

    const cleanedForm = cleanObject(form)

    // initialError obj for useFormValidation
    const errors: IForm = {
        first_name: "",
        last_name: "",
        callingCode: "",
        phone: "",
        email: "",
        nationality: "",
        country: "",
        city: "",
        gender: "",
        birth_date: "",
    }

    const { updateUser } = useUserContext()

    const { isLoading, mutate } = updateUser
    const [isLoadingState, setIsLoadingState] = useState(false)

    const isPhoneVerified = user?.verification?.is_phone_verified
    const isEmailVerified = user?.verification?.is_email_verified

    const onSubmit = () => {
        setIsLoadingState(true)
        mutate(state, {
            onSuccess: () => {
                //hide after 3 seconds
                Swal.fire({
                    title: t("profile_update_success_title"),
                    text: t("profile_update_success_message"),
                    icon: "success",
                    showConfirmButton: false,
                    confirmButtonText: t("ok"),
                    confirmButtonColor: "#FD0058",
                    backdrop: true,
                    background: "rgb(15 22 54)",
                    color: "#fff",
                    timer: 1500,
                    customClass: {
                        popup: "shadow-xl",
                        confirmButton:
                            "rounded-full transition-all duration-200 border text-sm base:text-base flex items-center justify-center bg-action-theme-400 hover:bg-action-theme-500 text-white border-transparent mt-8 w-full p-2 sm:w-[200px]",
                    },
                }).then(() => {
                    localStorage.setItem(
                        "pass_ticket_user",
                        JSON.stringify(state)
                    )
                    setIsLoadingState(false)
                    window.location.reload()
                })
            },
            onError: (error: any) => {
                let errorMessage = t("profile_update_error_message")

                switch (error.message) {
                    case "isPhoneExist":
                        errorMessage = t("phone_number_already_exists")
                        setStateErrors((prev) => ({
                            ...prev,
                            phone: t("phone_number_already_exists"),
                        }))
                        break
                    case "isEmailExist":
                        errorMessage = t("email_already_exists")
                        setStateErrors((prev) => ({
                            ...prev,
                            email: t("email_already_exists"),
                        }))
                        break
                    default:
                        break
                }

                Swal.fire({
                    title: t("profile_update_error_title"),
                    text: errorMessage,
                    icon: "error",
                    showConfirmButton: false,
                    confirmButtonText: t("ok"),
                    confirmButtonColor: "#FD0058",
                    backdrop: true,
                    background: "rgb(15 22 54)",
                    color: "#fff",
                    timer: 1500,
                    customClass: {
                        popup: "shadow-xl",
                    },
                }).then(() => {
                    setIsLoadingState(false)
                })
            },
        })
    }

    const { state, setState, stateErrors, setStateErrors, handleSubmit } =
        useFormValidation(cleanedForm, errors, validations, onSubmit)

    const showCity =
        state.country === "Saudi Arabia" ||
        state.country === "المملكة العربية السعودية"

    const onChange = (e: { target: { name: string; value: string } }) => {
        const { name, value } = e.target
        setState((prev) => ({ ...prev, [name]: value }))
        setStateErrors((prev) => ({ ...prev, [name]: "" }))
    }
    const onChangePhone = (name: "phone" | "callingCode", value: string) => {
        setState((prev) => ({ ...prev, [name]: value }))
        setStateErrors((prev) => ({ ...prev, [name]: "" }))
    }

    useEffect(() => {
        setMounted(true)
    }, [])

    if (!mounted) {
        return null
    }

    return (
        <div className="w-full">
            <div className="flex flex-col gap-8">
                {/* <form> */}
                <div className="grid items-start gap-5 md:grid-cols-2">
                    <InputNew
                        label={t("lb_First_Name")}
                        name={"first_name"}
                        type="text"
                        value={state.first_name}
                        error={stateErrors.first_name}
                        onChange={onChange}
                        reqStar
                    />
                    <InputNew
                        label={t("lb_Last_Name")}
                        name={"last_name"}
                        type="text"
                        value={state.last_name}
                        error={stateErrors.last_name}
                        onChange={onChange}
                        reqStar
                    />

                    <div className="flex flex-col gap-2">
                        <PhoneInput
                            callingCode={state.callingCode}
                            setCallingCode={(callingCode) =>
                                onChangePhone("callingCode", callingCode)
                            }
                            setPhone={(phone) => onChangePhone("phone", phone)}
                            phone={state.phone}
                            error={stateErrors.phone}
                            required
                        />

                        {!isPhoneVerified ? (
                            <div className="flex justify-between text-xs">
                                <span className="text-red-500">
                                    {t("verify_your_phone")}
                                </span>
                                <span
                                    onClick={() => setOpenModal(true)}
                                    className="cursor-pointer text-blue-500 underline dark:text-blue-400"
                                >
                                    {t("btn_verify")}
                                </span>
                            </div>
                        ) : null}
                    </div>

                    <div className="flex flex-col gap-2">
                        <InputNew
                            label={t("lb_email")}
                            name="email"
                            type="text"
                            value={state.email}
                            error={stateErrors.email}
                            onChange={onChange}
                            reqStar
                        />
                        {!isEmailVerified ? (
                            <div className="flex justify-between text-xs">
                                <span className="text-red-500">
                                    {t("verify_your_email")}
                                </span>
                                <span
                                    onClick={() => setOpenModal(true)}
                                    className="cursor-pointer text-blue-500 underline"
                                >
                                    {t("btn_verify")}
                                </span>
                            </div>
                        ) : null}
                    </div>

                    <ReusableSelect
                        label={t("lb_Nationality")}
                        name={"nationality"}
                        type="nationality"
                        value={state.nationality}
                        onChange={(value) =>
                            setState((prev) => ({
                                ...prev,
                                nationality: value,
                            }))
                        }
                    />
                    <ReusableSelect
                        label={t("lb_Country")}
                        name={"country"}
                        type="country"
                        value={state.country}
                        onChange={(value) => {
                            setState((prev) => ({
                                ...prev,
                                country: value,
                                city: "",
                            }))
                            setStateErrors((prev) => ({
                                ...prev,
                                country: "",
                                city: "",
                            }))
                        }}
                    />
                    {showCity ? (
                        <ReusableSelect
                            label={t("lb_City")}
                            name={"city"}
                            type="city"
                            value={state.city}
                            onChange={(value) =>
                                setState((prev) => ({
                                    ...prev,
                                    city: value,
                                }))
                            }
                        />
                    ) : null}

                    {/* Gender */}
                    <div className="mb-4 flex flex-col">
                        <p className="mb-1">{t("lb_Gender")}</p>
                        <div className="flex items-center gap-5">
                            <Input
                                id="gender-male"
                                label={t("pl_Gender_Male")}
                                name="gender"
                                type="radio"
                                value="male"
                                onChange={onChange}
                                required={false}
                                checked={
                                    state.gender?.toLocaleLowerCase() === "male"
                                }
                            />

                            <Input
                                id="gender-female"
                                label={t("pl_Gender_Female")}
                                name="gender"
                                type="radio"
                                value="female"
                                onChange={onChange}
                                required={false}
                                checked={
                                    state.gender?.toLocaleLowerCase() ===
                                    "female"
                                }
                            />
                        </div>
                    </div>
                    {/** DoB */}
                    <div className="mb-4 flex flex-col w-full items-start">
                        <p className="mb-1">{t("birth_date")}</p>
                        <div className="flex">
                            <DateOfBirthInput
                                value={{
                                    year: user?.birth_date
                                        ? dayjs(user.birth_date).year()
                                        : dayjs().year(),
                                    month: user?.birth_date
                                        ? dayjs(user.birth_date).month() + 1
                                        : dayjs().month() + 1,
                                    day: user?.birth_date
                                        ? dayjs(user.birth_date).date()
                                        : dayjs().date(),
                                }}
                                onChange={(value) => {
                                    setState((prev) => ({
                                        ...prev,
                                        birth_date: value,
                                    }))
                                }}
                                error={undefined}
                            />
                        </div>
                    </div>
                    <div className="mb-4 flex h-3/4 items-end md:mb-0">
                        <DeleteAccount />
                    </div>
                </div>

                <Button
                    text={t("Save")}
                    className="mt-8 w-full p-2 sm:w-[200px]"
                    type="button"
                    onClick={handleSubmit}
                    loading={isLoading || isLoadingState}
                />
                {/* </form> */}
            </div>

            <VerifyAccModal
                setIsModalOpen={setOpenModal}
                isModalOpen={openModal}
            />
        </div>
    )
}
